/**
 * A tool page's `qc.*` values become the settings of the reading-speed REPORT.
 *
 * /subtitle-reading-speed-checker published three controls (the standard, the
 * characters-per-second limit, whether spaces count) over a pipeline of
 * `['normalize']`. No op has those fields and nothing on the page asked the
 * worker for a QC report, so the controls reached nothing: the page normalised
 * the file, showed its cues, and measured no reading speed at all, whatever
 * the user chose.
 *
 * A report is not an op. `qc` is registered through `registerQc`, takes
 * `QcSettings` beside a document, and returns findings rather than a document,
 * so it cannot be a pipeline step and these values cannot go through
 * `opsFromValues`. This is the same translation for the other kind of engine,
 * kept beside it in `src/tools/` for the same reason: only the side that owns
 * `QcProfile` knows that "Netflix — Chinese" means 11 cps over 32 cells.
 *
 * THE RULE THE PAGE FOLLOWS: a schema with a `qc.` field is a page that runs
 * the report after every run. `consumerOf` in ops-from-values.ts uses the same
 * prefix, so the guard that every published key reaches something covers
 * these keys too.
 */

import type { MetricsCache, QcProfile, QcReport, QcSettings } from '../../contracts/cue-model.ts';
import { TICKS_PER_SECOND } from '../core/time/ticks.ts';
import { NAMED_PROFILES, type NamedProfile, PROFILE_LATIN } from '../transforms/clean/defaults.ts';

/** The key prefix that configures the QC report rather than an op. */
export const QC_NAMESPACE = 'qc';

const PROFILE_KEY = `${QC_NAMESPACE}.profile`;
const MAX_CPS_KEY = `${QC_NAMESPACE}.maxCps`;
const COUNT_SPACES_KEY = `${QC_NAMESPACE}.countSpaces`;

/** True when any of `keys` configures the QC report: the page has a report to show. */
export function hasQcControls(keys: Iterable<string>): boolean {
  for (const key of keys) if (key.startsWith(`${QC_NAMESPACE}.`)) return true;
  return false;
}

function profileNamed(name: unknown): QcProfile {
  return typeof name === 'string' && Object.hasOwn(NAMED_PROFILES, name)
    ? NAMED_PROFILES[name as NamedProfile]
    : PROFILE_LATIN;
}

/**
 * The settings `qc()` evaluates against.
 *
 * The standard supplies every threshold (line width, duration, gap, the floor)
 * and the two boxes override the two the page shows. They are always present
 * once the panel has mounted, so they always win; `qcProfileFollowers` is what
 * keeps them agreeing with the standard the user picks.
 *
 * Only `default` is set. A track that declares its language is measured by
 * that language's profile inside `qc()` (`profileFor` in src/qc/qc.ts), which
 * the page cannot override without per-track ids it does not hold across runs.
 */
export function qcSettingsFromValues(values: Readonly<Record<string, unknown>>): QcSettings {
  const base = profileNamed(values[PROFILE_KEY]);
  const maxCps = values[MAX_CPS_KEY];
  const countSpaces = values[COUNT_SPACES_KEY];
  return {
    default: {
      ...base,
      maxCps: typeof maxCps === 'number' && maxCps > 0 ? maxCps : base.maxCps,
      countSpaces: typeof countSpaces === 'boolean' ? countSpaces : base.countSpaces,
    },
  };
}

/**
 * The box values to write when the standard changes, or `null` when it did not.
 *
 * The limit box defaults to 20 (the latin standard's) and the boxes win over
 * the standard, so without this, choosing "Netflix — Chinese (11 characters/
 * second)" kept flagging at 20 with spaces counted: the choice's own label
 * would be false. Picking a standard sets both boxes to its numbers; the user
 * can still edit either afterwards, and that edit stands until the next pick.
 */
export function qcProfileFollowers(
  previous: Readonly<Record<string, unknown>>,
  next: Readonly<Record<string, unknown>>,
): Readonly<Record<string, number | boolean>> | null {
  if (!(PROFILE_KEY in next) || previous[PROFILE_KEY] === next[PROFILE_KEY]) return null;
  // A first emit (nothing before it) is the panel's defaults, which already agree.
  if (!(PROFILE_KEY in previous)) return null;
  const profile = profileNamed(next[PROFILE_KEY]);
  return { [MAX_CPS_KEY]: profile.maxCps, [COUNT_SPACES_KEY]: profile.countSpaces };
}

/** One cue's reading speed as the table shows it. */
export interface CueSpeed {
  /** Characters per second under the page's settings; Infinity for a zero-length cue. */
  readonly cps: number;
  /** `qc()` flagged it 'too-fast'. The ENGINE's verdict, not a re-derivation. */
  readonly over: boolean;
}

/**
 * Per-cue speed, keyed by cue id, for the table's metric column.
 *
 * The numerator is what `cpsOf` in src/qc/qc.ts uses (clusters, with or without
 * spaces) so the number shown is the number judged. `over` is read from
 * `flagsByCue` rather than recomputed, so the column cannot disagree with the
 * report's own count. Empty cues are left out, as `qc()` leaves them out of
 * every speed figure.
 */
export function cueSpeeds(report: QcReport, settings: QcSettings): ReadonlyMap<string, CueSpeed> {
  const out = new Map<string, CueSpeed>();
  const perCue: MetricsCache = report.perCue;
  const countSpaces = settings.default.countSpaces;
  for (const metrics of Object.values(perCue)) {
    if (metrics === undefined) continue;
    const chars = countSpaces ? metrics.totalClusters : metrics.totalClustersNoSpace;
    if (chars === 0) continue;
    const seconds = (metrics.durationTicks as number) / TICKS_PER_SECOND;
    const flags = report.flagsByCue[metrics.cueId] ?? [];
    out.set(metrics.cueId, {
      cps: seconds > 0 ? chars / seconds : Number.POSITIVE_INFINITY,
      over: flags.includes('too-fast'),
    });
  }
  return out;
}

/** The metric cell's text: one decimal, which is how every guide states its limit. */
export function formatCps(cps: number): string {
  return Number.isFinite(cps) ? cps.toFixed(1) : '—';
}
